import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  Users, Search, Eye, CheckCircle, XCircle, Clock, FileText,
  UserPlus, Palette, HandCoins, Mail, Phone, Building2, Calendar,
  Loader2, AlertCircle, Video, PhoneCall, MapPin, CalendarPlus,
  CalendarCheck, ThumbsUp, ThumbsDown, User, ChevronRight,
  ArrowUpDown, ChevronUp, ChevronDown, MoreHorizontal
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Using api from lib/axios

const STATUS_CONFIG = {
  questionnaire: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  contract_pending: { label: 'Contrat en attente', color: 'bg-blue-100 text-blue-700', icon: FileText },
  contract_signed: { label: 'Contrat signé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  interview_scheduled: { label: 'Entretien planifié', color: 'bg-purple-100 text-purple-700', icon: CalendarCheck },
  interview_done: { label: 'Entretien terminé', color: 'bg-indigo-100 text-indigo-700', icon: Video },
  validated: { label: 'Validé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const INTERVIEW_TYPES = [
  { value: 'video', label: 'Visioconférence', icon: Video },
  { value: 'phone', label: 'Téléphone', icon: PhoneCall },
  { value: 'in_person', label: 'En personne', icon: MapPin },
];

const OnboardingManagementPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('creatives');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showInterviewResultModal, setShowInterviewResultModal] = useState(false);
  
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Interview form state
  const [interviewForm, setInterviewForm] = useState({
    interview_date: '',
    interview_time: '',
    interview_type: 'video',
    notes: '',
    interviewer_name: ''
  });
  
  // Interview result form state
  const [interviewResult, setInterviewResult] = useState({
    result: 'passed',
    notes: '',
    recommendation: 'hire'
  });

  // Open detail modal if onboarding ID is in URL params
  useEffect(() => {
    const onboardingId = searchParams.get('id');
    if (onboardingId && onboardings.length > 0) {
      const target = onboardings.find(o => o.id === onboardingId);
      if (target) {
        setSelectedOnboarding(target);
        setShowDetailModal(true);
        // Set the correct tab based on type
        setActiveTab(target.type === 'creative' ? 'creatives' : 'affiliates');
        // Clear the URL param
        setSearchParams({});
      }
    }
  }, [searchParams, onboardings, setSearchParams]);

  useEffect(() => {
    fetchOnboardings();
    fetchStats();
  }, []);

  const fetchOnboardings = async () => {
    try {
      const response = await api.get('/onboarding/');
      setOnboardings(response.data);
    } catch (error) {
      console.error('Error fetching onboardings:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/onboarding/stats/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleValidate = async (onboarding) => {
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/onboarding/${onboarding.id}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Validé ! Mot de passe temporaire: ${response.data.temp_password}`);
      fetchOnboardings();
      fetchStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/${selectedOnboarding.id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Candidature rejetée');
      fetchOnboardings();
      fetchStats();
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du rejet');
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.interview_date || !interviewForm.interview_time) {
      toast.error('Veuillez sélectionner une date et une heure');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/${selectedOnboarding.id}/schedule-interview`,
        interviewForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Entretien planifié avec succès');
      fetchOnboardings();
      fetchStats();
      setShowInterviewModal(false);
      setShowDetailModal(false);
      setInterviewForm({
        interview_date: '',
        interview_time: '',
        interview_type: 'video',
        notes: '',
        interviewer_name: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la planification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitInterviewResult = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/${selectedOnboarding.id}/interview-result`,
        interviewResult,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Résultat de l\'entretien enregistré');
      fetchOnboardings();
      fetchStats();
      setShowInterviewResultModal(false);
      setShowDetailModal(false);
      setInterviewResult({ result: 'passed', notes: '', recommendation: 'hire' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter onboardings by type and status
  const getFilteredOnboardings = (type) => {
    return onboardings.filter(o => {
      const matchesType = o.type === type;
      const matchesStatus = filterStatus === 'all' || o.status === filterStatus;
      const q = o.questionnaire || {};
      const matchesSearch = !searchQuery || 
        `${q.first_name} ${q.last_name} ${q.email}`.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesStatus && matchesSearch;
    });
  };

  const creativeOnboardings = getFilteredOnboardings('creative');
  const affiliateOnboardings = getFilteredOnboardings('affiliate');

  const currentStats = stats?.[activeTab === 'creatives' ? 'creatives' : 'affiliates'] || {};

  const renderOnboardingCard = (onboarding) => {
    const q = onboarding.questionnaire || {};
    const statusConfig = STATUS_CONFIG[onboarding.status] || STATUS_CONFIG.questionnaire;
    const StatusIcon = statusConfig.icon;
    const interview = onboarding.interview;
    
    return (
      <Card key={onboarding.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
              onboarding.type === 'creative' 
                ? "bg-gradient-to-br from-cyan-500 to-blue-600" 
                : "bg-gradient-to-br from-orange-500 to-amber-600"
            )}>
              {onboarding.type === 'creative' 
                ? <Palette className="w-6 h-6 text-white" />
                : <HandCoins className="w-6 h-6 text-white" />
              }
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-semibold text-slate-800 truncate">
                  {q.first_name} {q.last_name}
                </h3>
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[150px]">{q.email}</span>
                </span>
                {q.phone && (
                  <span className="flex items-center gap-1 hidden sm:flex">
                    <Phone className="w-3.5 h-3.5" />
                    {q.phone}
                  </span>
                )}
              </div>
              
              {/* Interview info */}
              {interview && onboarding.status === 'interview_scheduled' && (
                <div className="flex items-center gap-2 mt-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-lg w-fit">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>
                    Entretien le {new Date(interview.scheduled_date).toLocaleDateString('fr-FR')} à {interview.scheduled_time}
                  </span>
                </div>
              )}
            </div>
            
            <div className="text-right hidden md:block shrink-0">
              <p className="text-xs text-slate-400">
                {new Date(onboarding.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedOnboarding(onboarding);
                setShowDetailModal(true);
              }}
              className="rounded-full shrink-0"
            >
              <Eye className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Voir</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderWorkflowStep = (label, status, isActive, isCompleted) => (
    <div className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
      isCompleted && "bg-green-50 text-green-700",
      isActive && !isCompleted && "bg-blue-50 text-blue-700 ring-2 ring-blue-200",
      !isActive && !isCompleted && "bg-slate-50 text-slate-400"
    )}>
      {isCompleted ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <div className={cn(
          "w-4 h-4 rounded-full border-2",
          isActive ? "border-blue-500" : "border-slate-300"
        )} />
      )}
      <span>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Onboarding Partenaires
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez les candidatures et planifiez les entretiens
              </p>
            </div>
          </div>

          {/* Main Tabs: Créatifs / Apporteurs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="creatives" className="gap-2">
                <Palette className="w-4 h-4" />
                Créatifs
                {stats?.creatives?.pending_action > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {stats.creatives.pending_action}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="affiliates" className="gap-2">
                <HandCoins className="w-4 h-4" />
                Apporteurs
                {stats?.affiliates?.pending_action > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {stats.affiliates.pending_action}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Stats for current tab */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Users className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.total || 0}</p>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.contract_signed || 0}</p>
                      <p className="text-xs text-slate-500">Signés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                      <CalendarCheck className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.interview_scheduled || 0}</p>
                      <p className="text-xs text-slate-500">Entretiens</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Video className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.interview_done || 0}</p>
                      <p className="text-xs text-slate-500">Terminés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.validated || 0}</p>
                      <p className="text-xs text-slate-500">Validés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <XCircle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold">{currentStats.rejected || 0}</p>
                      <p className="text-xs text-slate-500">Rejetés</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Rechercher par nom ou email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="questionnaire">En attente</SelectItem>
                      <SelectItem value="contract_signed">Contrat signé</SelectItem>
                      <SelectItem value="interview_scheduled">Entretien planifié</SelectItem>
                      <SelectItem value="interview_done">Entretien terminé</SelectItem>
                      <SelectItem value="validated">Validé</SelectItem>
                      <SelectItem value="rejected">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Créatifs Tab */}
            <TabsContent value="creatives" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : creativeOnboardings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Palette className="w-12 h-12 text-cyan-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun créatif</h3>
                    <p className="text-slate-500">Les candidatures de créatifs apparaîtront ici</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Candidat</TableHead>
                          <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                          <TableHead className="font-semibold text-slate-700">Spécialité</TableHead>
                          <TableHead className="font-semibold text-slate-700">Statut</TableHead>
                          <TableHead className="font-semibold text-slate-700">Entretien</TableHead>
                          <TableHead className="font-semibold text-slate-700">Date</TableHead>
                          <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creativeOnboardings.map((onboarding) => {
                          const q = onboarding.questionnaire || {};
                          const statusConfig = STATUS_CONFIG[onboarding.status] || STATUS_CONFIG.questionnaire;
                          const StatusIcon = statusConfig.icon;
                          const interview = onboarding.interview;
                          
                          return (
                            <TableRow 
                              key={onboarding.id}
                              className="cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => { setSelectedOnboarding(onboarding); setShowDetailModal(true); }}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                                    <Palette className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">{q.first_name} {q.last_name}</p>
                                    <p className="text-xs text-slate-500">{q.company_name || 'Indépendant'}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="text-slate-600 truncate max-w-[150px]">{q.email}</p>
                                  <p className="text-slate-400 text-xs">{q.phone || '-'}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600">{q.specialty || q.skills?.[0] || '-'}</span>
                              </TableCell>
                              <TableCell>
                                <Badge className={statusConfig.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {interview && onboarding.status === 'interview_scheduled' ? (
                                  <span className="text-xs text-purple-600 whitespace-nowrap">
                                    {new Date(interview.scheduled_date).toLocaleDateString('fr-FR')} - {interview.scheduled_time}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-500 whitespace-nowrap">
                                  {new Date(onboarding.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => { setSelectedOnboarding(onboarding); setShowDetailModal(true); }}
                                  >
                                    <Eye className="w-4 h-4 text-slate-500" />
                                  </Button>
                                  {onboarding.status === 'questionnaire' && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={() => handleValidate(onboarding)}
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => { setSelectedOnboarding(onboarding); setShowRejectModal(true); }}
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Apporteurs Tab */}
            <TabsContent value="affiliates" className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : affiliateOnboardings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <HandCoins className="w-12 h-12 text-orange-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun apporteur d'affaires</h3>
                    <p className="text-slate-500">Les candidatures d'apporteurs apparaîtront ici</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="font-semibold text-slate-700">Candidat</TableHead>
                          <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                          <TableHead className="font-semibold text-slate-700">Entreprise</TableHead>
                          <TableHead className="font-semibold text-slate-700">Statut</TableHead>
                          <TableHead className="font-semibold text-slate-700">Entretien</TableHead>
                          <TableHead className="font-semibold text-slate-700">Date</TableHead>
                          <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {affiliateOnboardings.map((onboarding) => {
                          const q = onboarding.questionnaire || {};
                          const statusConfig = STATUS_CONFIG[onboarding.status] || STATUS_CONFIG.questionnaire;
                          const StatusIcon = statusConfig.icon;
                          const interview = onboarding.interview;
                          
                          return (
                            <TableRow 
                              key={onboarding.id}
                              className="cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => { setSelectedOnboarding(onboarding); setShowDetailModal(true); }}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shrink-0">
                                    <HandCoins className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">{q.first_name} {q.last_name}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <p className="text-slate-600 truncate max-w-[150px]">{q.email}</p>
                                  <p className="text-slate-400 text-xs">{q.phone || '-'}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600">{q.company_name || '-'}</span>
                              </TableCell>
                              <TableCell>
                                <Badge className={statusConfig.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {interview && onboarding.status === 'interview_scheduled' ? (
                                  <span className="text-xs text-purple-600 whitespace-nowrap">
                                    {new Date(interview.scheduled_date).toLocaleDateString('fr-FR')} - {interview.scheduled_time}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-500 whitespace-nowrap">
                                  {new Date(onboarding.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => { setSelectedOnboarding(onboarding); setShowDetailModal(true); }}
                                  >
                                    <Eye className="w-4 h-4 text-slate-500" />
                                  </Button>
                                  {onboarding.status === 'questionnaire' && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                        onClick={() => handleValidate(onboarding)}
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => { setSelectedOnboarding(onboarding); setShowRejectModal(true); }}
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedOnboarding?.type === 'creative' 
                ? <Palette className="w-5 h-5 text-cyan-600" />
                : <HandCoins className="w-5 h-5 text-orange-600" />
              }
              {selectedOnboarding?.questionnaire?.first_name} {selectedOnboarding?.questionnaire?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOnboarding && (
            <>
              {/* Workflow Progress */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <p className="text-xs text-slate-500 mb-3 uppercase tracking-wide">Progression du dossier</p>
                <div className="flex flex-wrap gap-2">
                  {renderWorkflowStep('Questionnaire', 'questionnaire', 
                    selectedOnboarding.status === 'questionnaire',
                    ['contract_signed', 'interview_scheduled', 'interview_done', 'validated'].includes(selectedOnboarding.status)
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300 self-center hidden sm:block" />
                  {renderWorkflowStep('Contrat signé', 'contract_signed',
                    selectedOnboarding.status === 'contract_signed',
                    ['interview_scheduled', 'interview_done', 'validated'].includes(selectedOnboarding.status)
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300 self-center hidden sm:block" />
                  {renderWorkflowStep('Entretien', 'interview_scheduled',
                    ['interview_scheduled', 'interview_done'].includes(selectedOnboarding.status),
                    ['interview_done', 'validated'].includes(selectedOnboarding.status)
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300 self-center hidden sm:block" />
                  {renderWorkflowStep('Validé', 'validated',
                    selectedOnboarding.status === 'validated',
                    selectedOnboarding.status === 'validated'
                  )}
                </div>
              </div>

              {/* Interview Info */}
              {selectedOnboarding.interview && (
                <div className={cn(
                  "rounded-xl p-4 mb-4",
                  selectedOnboarding.status === 'interview_scheduled' ? "bg-purple-50 border border-purple-200" : "bg-indigo-50 border border-indigo-200"
                )}>
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarCheck className="w-5 h-5 text-purple-600" />
                    <h4 className="font-medium text-slate-800">Informations entretien</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Date</p>
                      <p className="font-medium">{new Date(selectedOnboarding.interview.scheduled_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Heure</p>
                      <p className="font-medium">{selectedOnboarding.interview.scheduled_time}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Type</p>
                      <p className="font-medium capitalize">
                        {INTERVIEW_TYPES.find(t => t.value === selectedOnboarding.interview.type)?.label || selectedOnboarding.interview.type}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Intervieweur</p>
                      <p className="font-medium">{selectedOnboarding.interview.interviewer_name || '-'}</p>
                    </div>
                    {selectedOnboarding.interview.result && (
                      <>
                        <div className="col-span-2 pt-2 border-t">
                          <p className="text-slate-500">Résultat</p>
                          <Badge className={selectedOnboarding.interview.result === 'passed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {selectedOnboarding.interview.result === 'passed' ? 'Réussi' : 'Échoué'}
                          </Badge>
                        </div>
                        {selectedOnboarding.interview.result_notes && (
                          <div className="col-span-2">
                            <p className="text-slate-500">Notes</p>
                            <p className="font-medium">{selectedOnboarding.interview.result_notes}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              <Tabs defaultValue="info" className="mt-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Informations</TabsTrigger>
                  <TabsTrigger value="pro">Professionnel</TabsTrigger>
                  <TabsTrigger value="motivation">Motivation</TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500">Prénom</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.first_name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Nom</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.last_name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Email</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.email}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Téléphone</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.phone}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-slate-500">Adresse</label>
                      <p className="font-medium">
                        {selectedOnboarding.questionnaire?.address}, {selectedOnboarding.questionnaire?.postal_code} {selectedOnboarding.questionnaire?.city}
                      </p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="pro" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-500">Statut juridique</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.legal_status}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Entreprise</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.company_name || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">SIRET</label>
                      <p className="font-medium font-mono">{selectedOnboarding.questionnaire?.siret || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Disponibilité</label>
                      <p className="font-medium">{selectedOnboarding.questionnaire?.availability}</p>
                    </div>
                  </div>
                  
                  {selectedOnboarding.type === 'creative' && (
                    <>
                      <div>
                        <label className="text-xs text-slate-500">Spécialités</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedOnboarding.questionnaire?.specialties?.map(s => (
                            <Badge key={s} variant="secondary">{s}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-slate-500">Années d'exp.</label>
                          <p className="font-medium">{selectedOnboarding.questionnaire?.years_experience} ans</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Portfolio</label>
                          {selectedOnboarding.questionnaire?.portfolio_url ? (
                            <a href={selectedOnboarding.questionnaire.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                              Voir →
                            </a>
                          ) : <p className="font-medium">-</p>}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {selectedOnboarding.type === 'affiliate' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-slate-500">Possède un réseau</label>
                        <p className="font-medium">{selectedOnboarding.questionnaire?.has_network ? 'Oui' : 'Non'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Estimé de referrals/mois</label>
                        <p className="font-medium">{selectedOnboarding.questionnaire?.estimated_referrals || 0}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-medium mb-2">Coordonnées bancaires</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">IBAN:</span> <span className="font-mono">{selectedOnboarding.questionnaire?.iban}</span></p>
                      <p><span className="text-slate-500">BIC:</span> <span className="font-mono">{selectedOnboarding.questionnaire?.bic}</span></p>
                      <p><span className="text-slate-500">Banque:</span> {selectedOnboarding.questionnaire?.bank_name}</p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="motivation" className="space-y-4 mt-4">
                  <div>
                    <label className="text-xs text-slate-500">Bio</label>
                    <p className="mt-1">{selectedOnboarding.questionnaire?.bio || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Motivation</label>
                    <p className="mt-1 whitespace-pre-wrap">{selectedOnboarding.questionnaire?.motivation}</p>
                  </div>
                  
                  {selectedOnboarding.signature_date && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-emerald-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Contrat signé le {new Date(selectedOnboarding.signature_date).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
          
          <DialogFooter className="mt-6 flex-wrap gap-2">
            {/* Actions based on status */}
            {selectedOnboarding?.status === 'contract_signed' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectModal(true)}
                  className="rounded-full text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInterviewModal(true)}
                  className="rounded-full text-purple-600 hover:text-purple-700"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Planifier entretien
                </Button>
                <Button
                  onClick={() => handleValidate(selectedOnboarding)}
                  disabled={actionLoading}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Valider directement
                </Button>
              </>
            )}
            
            {selectedOnboarding?.status === 'interview_scheduled' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectModal(true)}
                  className="rounded-full text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  onClick={() => setShowInterviewResultModal(true)}
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                >
                  <Video className="w-4 h-4 mr-2" />
                  Saisir résultat entretien
                </Button>
              </>
            )}
            
            {selectedOnboarding?.status === 'interview_done' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectModal(true)}
                  className="rounded-full text-red-600 hover:text-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeter
                </Button>
                <Button
                  onClick={() => handleValidate(selectedOnboarding)}
                  disabled={actionLoading}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Valider et créer compte
                </Button>
              </>
            )}
            
            {selectedOnboarding?.status === 'validated' && (
              <Badge className="bg-green-100 text-green-700 text-sm px-4 py-2">
                <CheckCircle className="w-4 h-4 mr-1" />
                Déjà validé
              </Badge>
            )}
            
            {selectedOnboarding?.status === 'rejected' && (
              <div className="w-full">
                <Badge className="bg-red-100 text-red-700 text-sm px-4 py-2">
                  <XCircle className="w-4 h-4 mr-1" />
                  Rejeté
                </Badge>
                {selectedOnboarding.rejection_reason && (
                  <p className="text-sm text-slate-500 mt-2">Raison: {selectedOnboarding.rejection_reason}</p>
                )}
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Modal */}
      <Dialog open={showInterviewModal} onOpenChange={setShowInterviewModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <CalendarPlus className="w-5 h-5" />
              Planifier un entretien
            </DialogTitle>
            <DialogDescription>
              Planifiez un entretien avec {selectedOnboarding?.questionnaire?.first_name} {selectedOnboarding?.questionnaire?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={interviewForm.interview_date}
                  onChange={(e) => setInterviewForm({ ...interviewForm, interview_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Heure *</Label>
                <Input
                  type="time"
                  value={interviewForm.interview_time}
                  onChange={(e) => setInterviewForm({ ...interviewForm, interview_time: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Type d'entretien</Label>
              <Select value={interviewForm.interview_type} onValueChange={(val) => setInterviewForm({ ...interviewForm, interview_type: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVIEW_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Intervieweur</Label>
              <Input
                value={interviewForm.interviewer_name}
                onChange={(e) => setInterviewForm({ ...interviewForm, interviewer_name: e.target.value })}
                placeholder="Nom de l'intervieweur"
              />
            </div>
            
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={interviewForm.notes}
                onChange={(e) => setInterviewForm({ ...interviewForm, notes: e.target.value })}
                placeholder="Points à aborder, lien visio, etc."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInterviewModal(false)} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={handleScheduleInterview}
              disabled={actionLoading}
              className="rounded-full bg-purple-600 hover:bg-purple-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarCheck className="w-4 h-4 mr-2" />}
              Planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Interview Result Modal */}
      <Dialog open={showInterviewResultModal} onOpenChange={setShowInterviewResultModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Video className="w-5 h-5" />
              Résultat de l'entretien
            </DialogTitle>
            <DialogDescription>
              Enregistrez le résultat de l'entretien avec {selectedOnboarding?.questionnaire?.first_name} {selectedOnboarding?.questionnaire?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Résultat de l'entretien</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setInterviewResult({ ...interviewResult, result: 'passed' })}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all",
                    interviewResult.result === 'passed' 
                      ? "border-green-500 bg-green-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <ThumbsUp className={cn("w-6 h-6 mb-2", interviewResult.result === 'passed' ? "text-green-600" : "text-slate-400")} />
                  <div className="font-medium">Réussi</div>
                  <div className="text-xs text-slate-500">Le candidat convient</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setInterviewResult({ ...interviewResult, result: 'failed' })}
                  className={cn(
                    "p-4 rounded-xl border-2 text-left transition-all",
                    interviewResult.result === 'failed' 
                      ? "border-red-500 bg-red-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <ThumbsDown className={cn("w-6 h-6 mb-2", interviewResult.result === 'failed' ? "text-red-600" : "text-slate-400")} />
                  <div className="font-medium">Échoué</div>
                  <div className="text-xs text-slate-500">Ne convient pas</div>
                </button>
              </div>
            </div>
            
            <div>
              <Label>Recommandation</Label>
              <Select value={interviewResult.recommendation} onValueChange={(val) => setInterviewResult({ ...interviewResult, recommendation: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hire">✅ Embaucher</SelectItem>
                  <SelectItem value="maybe">🤔 À reconsidérer</SelectItem>
                  <SelectItem value="reject">❌ Rejeter</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes et observations</Label>
              <Textarea
                value={interviewResult.notes}
                onChange={(e) => setInterviewResult({ ...interviewResult, notes: e.target.value })}
                placeholder="Points positifs, points à améliorer, observations..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInterviewResultModal(false)} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={handleSubmitInterviewResult}
              disabled={actionLoading}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Rejeter la candidature
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Veuillez indiquer la raison du rejet.
            </DialogDescription>
          </DialogHeader>
          
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Raison du rejet..."
            className="min-h-[100px]"
          />
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="rounded-full bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirmer le rejet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingManagementPage;
